import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '../../lib/cn';

interface CodeBlockProps {
  code: string;
  language?: string;
  label?: string;
  /** Whether to show the copy button (default true). */
  copyable?: boolean;
  /** Legacy alias of `copyable`. */
  showCopy?: boolean;
  className?: string;
  lineNumbers?: boolean;
}

export function CodeBlock({
  code,
  language: _language = 'text',
  label,
  copyable,
  showCopy,
  className,
  lineNumbers = false,
}: CodeBlockProps) {
  const canCopy = copyable ?? showCopy ?? true;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const lines = code.split('\n');

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-xl border border-stone-800 bg-stone-950 font-mono text-sm',
        className,
      )}
    >
      {/* Header */}
      {(label || canCopy) && (
        <div className="flex items-center justify-between border-b border-stone-800 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-red-500/70" />
            <span className="h-2 w-2 rounded-full bg-yellow-500/70" />
            <span className="h-2 w-2 rounded-full bg-green-500/70" />
            {label && (
              <span className="ml-2 text-[11px] font-medium text-stone-400">{label}</span>
            )}
          </div>
          {canCopy && (
            <button
              type="button"
              onClick={handleCopy}
              aria-label={copied ? 'Copié' : 'Copier'}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium text-stone-400 transition-colors hover:bg-stone-800 hover:text-stone-200"
            >
              {copied ? (
                <>
                  <Check className="h-3 w-3 text-brand-400" />
                  Copié
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3" />
                  Copier
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Code */}
      <div className="overflow-x-auto">
        <pre className="p-4 leading-relaxed text-stone-200">
          {lineNumbers ? (
            <code>
              {lines.map((line, i) => (
                <div key={i} className="flex gap-4">
                  <span className="select-none text-right text-[11px] text-stone-600 w-6 shrink-0">
                    {i + 1}
                  </span>
                  <span>{line}</span>
                </div>
              ))}
            </code>
          ) : (
            <code>{code}</code>
          )}
        </pre>
      </div>
    </div>
  );
}
